// Shared formatting + aggregation helpers for the Reports subsystem.
// Every user-facing date in the Reports section MUST use DD-MM-YYYY and every
// rice quantity MUST use 3-decimal precision. Keeping these in one place makes
// the whole surface trivially consistent.

/**
 * formatReportDate(input) → "DD-MM-YYYY"
 * Accepts a `Date`, an ISO string ("YYYY-MM-DD" or full ISO), or a millisecond timestamp.
 */
export function formatReportDate(input) {
  if (!input) return "";
  let d;
  if (input instanceof Date) d = input;
  else if (typeof input === "number") d = new Date(input);
  else if (typeof input === "string") {
    // Handle plain "YYYY-MM-DD" without TZ shifts
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    d = new Date(input);
  }
  if (!d || isNaN(d.getTime())) return String(input);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()}`;
}

/**
 * formatRiceQuantity(kg) → "12.500 kg". Rounds half-up to 3 decimals.
 * Does NOT alter the underlying number — presentation only.
 */
export function formatRiceQuantity(kg) {
  const n = Number(kg || 0);
  return `${n.toFixed(3)} kg`;
}

// Same as `formatRiceQuantity` but without the unit — for table cells that
// already have "Rice (kg)" in the column header.
export function formatRiceKg(kg) {
  return Number(kg || 0).toFixed(3);
}

// Canonical category keys + labels, used by every category-wise view.
export const CATEGORY_KEYS = ["balvatika", "primary", "upper_primary", "high_school"];
export const CATEGORY_LABELS = {
  balvatika: "Balvatika",
  primary: "Primary (I–V)",
  upper_primary: "Upper Primary (VI–VIII)",
  high_school: "High School (IX–X)",
};
export const CATEGORY_EMOJI = {
  balvatika: "🧒",
  primary: "📚",
  upper_primary: "📘",
  high_school: "🎓",
};

/**
 * aggregateCategory(entries, catKey) → { attendance, rice_kg, cost, working_days, mdm_days, dry_ration_days, holidays, avg }
 * Reuses server-persisted `entry.categories[cat]` values (never re-calculates).
 * Holidays contribute 0 attendance / 0 rice / 0 cost.
 */
export function aggregateCategory(entries, catKey) {
  let attendance = 0, rice = 0, cost = 0;
  let workingDays = 0, mdmDays = 0, dryRationDays = 0, holidays = 0;
  const rowsWithCategory = entries.filter((e) => (e.entry_type || "meal") === "meal");
  rowsWithCategory.forEach((e) => {
    if (e.day_type === "HOLIDAY") { holidays += 1; return; }
    workingDays += 1;
    if (e.meal_type === "DRY_RATION") dryRationDays += 1; else mdmDays += 1;
    const c = (e.categories || {})[catKey] || {};
    attendance += Number(c.student_count || e.counts?.[catKey] || 0);
    rice += Number(c.rice_kg || 0);
    cost += Number(c.subtotal || 0);
  });
  return {
    attendance, rice_kg: rice, cost,
    working_days: workingDays, mdm_days: mdmDays, dry_ration_days: dryRationDays, holidays,
    avg: workingDays > 0 ? attendance / workingDays : 0,
  };
}
